import { getPresetValues } from 'utils/api';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export interface IPreset {
  label: string;
  value: string;
}

interface PresetStoreProps {
  presetsLoaded: boolean;

  tollServiceProviderValues: IPreset[];
  tollServiceProviderOptions: IPreset[];

  tollChargerValues: IPreset[];
  actionCodeValues: IPreset[];
  actionListRecordTypeValues: IPreset[];
  actionListTypeValues: IPreset[];
  actionListStatusValues: IPreset[];
  ackTypeValues: IPreset[];
  paymentClaimTypeOfContentValues: IPreset[];

  passageTypeValues: IPreset[];
  passageStatusValues: IPreset[];
  localAgreementTypesValues: IPreset[];
}

interface PresetStoreActions {
  setPresetValues: (
    newTollChargers: IPreset[],
    newTollServiceProviders: IPreset[],
    newActionCodes: IPreset[],
    newAckTypes: IPreset[],
    newActionListRecordTypes: IPreset[],
    newActionListTypes: IPreset[],
    newActionListStatus: IPreset[],
    newPaymentClaimTypeOfContent: IPreset[],
    newPassageTypes: IPreset[],
    newPassageStatus: IPreset[],
    newLocalAgreementTypes: IPreset[]
  ) => void;
  fetchPresetValues: () => Promise<void>;
}

const usePresetStore = create(
  persist<PresetStoreProps & PresetStoreActions>(
    (set, get) => ({
      presetsLoaded: false,

      tollChargerValues: [],
      tollServiceProviderValues: [],
      tollServiceProviderOptions: [],

      actionCodeValues: [],
      ackTypeValues: [],
      actionListRecordTypeValues: [],
      actionListTypeValues: [],
      actionListStatusValues: [],
      paymentClaimTypeOfContentValues: [],

      passageTypeValues: [],
      passageStatusValues: [],
      localAgreementTypesValues: [],

      setPresetValues: (
        newTollChargers,
        newTollServiceProviders,
        newActionCodes,
        newAckTypes,
        newActionListRecordTypes,
        newActionListTypes,
        newActionListStatus,
        newPaymentClaimTypeOfContent,
        newPassageTypes,
        newPassageStatus,
        newLocalAgreementTypes
      ) => {
        const newTollServiceProviderOptions = newTollServiceProviders
          .map((tsp) => {
            return {
              label: `${tsp.label} - [${tsp.value}]`,
              value: tsp.value,
            } as IPreset;
          })
          .sort((a, b) => a.label.localeCompare(b.label));

        set({
          tollChargerValues: newTollChargers,
          tollServiceProviderValues: newTollServiceProviders,
          tollServiceProviderOptions: newTollServiceProviderOptions,
          actionCodeValues: newActionCodes,
          ackTypeValues: newAckTypes,
          actionListRecordTypeValues: newActionListRecordTypes,
          actionListTypeValues: newActionListTypes,
          actionListStatusValues: newActionListStatus,
          paymentClaimTypeOfContentValues: newPaymentClaimTypeOfContent,
          passageTypeValues: newPassageTypes,
          passageStatusValues: newPassageStatus,
          localAgreementTypesValues: newLocalAgreementTypes,
          presetsLoaded: true,
        });
      },
      fetchPresetValues: async () => {
        const presetResult = await getPresetValues();
        get().setPresetValues(
          presetResult.tollChargers ?? [],
          presetResult.tollServiceProviders ?? [],
          presetResult.actionCodes ?? [],
          presetResult.ackTypes ?? [],
          presetResult.actionListRecordTypes ?? [],
          presetResult.actionListTypes ?? [],
          presetResult.actionListStatus ?? [],
          presetResult.paymentClaimTypeOfContent ?? [],
          presetResult.passageType ?? [],
          presetResult.passageStatus ?? [],
          presetResult.localAgreementTypes ?? []
        );
      },
    }),
    {
      name: 'presetStorage',
      storage: createJSONStorage(() => localStorage),
    }
  )
);

export default usePresetStore;
